import {
  AlertCircle,
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  Info,
  RefreshCw,
  Ruler,
  SlidersHorizontal,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatRail } from "@/shared/utils/railDisplay";
import { colocaToast } from "@/shared/utils/toast";

import { AmountLimitsCards } from "../components/limits/AmountLimitsCards";
import { AmountLimitsTable } from "../components/limits/AmountLimitsTable";
import { EditLimitDialog, type EditLimitIntent } from "../components/limits/EditLimitDialog";
import { formatBound, rowsInScope } from "../components/limits/limitCellDisplay";
import { OrganizationScopePicker } from "../components/OrganizationScopePicker";
import { useLimitMutations } from "../hooks/useLimitMutations";
import { useLimitSettings } from "../hooks/useLimitSettings";
import { useOrganizationsCatalog } from "../hooks/useOrganizationsCatalog";
import type { AmountLimitCell } from "../types/paymentLimits.types";

type LimitFilter = "all" | "limited" | "overrides";

/**
 * `/app/payments-config/limits` — per-transaction amount limits, for INTERNAL staff.
 *
 * One matrix, two scopes, like the rails board. The GLOBAL view edits the bounds everyone inherits; picking
 * an organization shows what THAT client effectively gets and writes overrides for it — most-specific-wins,
 * so an org override beats the global rule in both senses (tighter or looser). Enforcement is the backend's:
 * an out-of-range amount is refused at preview/initiate (payins) and quote/create (payouts), while in-flight
 * operations are untouched.
 *
 * Freshness: no polling — limits change by human action only. Every mutation also drops the shared corridors
 * memo, so the payin forms and the dispersion wizard pick up the new bounds live.
 */
const AmountLimitsPage = () => {
  const { t } = useTranslations("paymentLimits");
  const { t: tGlobal, activeLanguage } = useTranslations("global");
  const isMobile = useIsMobile();

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_payment_limits"), to: "/app/payments-config/limits" },
      ],
      [tGlobal],
    ),
  );

  const [scopeOrgId, setScopeOrgId] = useState<string | null>(null);
  const [filter, setFilter] = useState<LimitFilter>("all");
  const [editIntent, setEditIntent] = useState<EditLimitIntent | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AmountLimitCell | null>(null);

  const state = useLimitSettings(scopeOrgId);
  const orgs = useOrganizationsCatalog();
  // Serves the remove dialog; the edit dialog owns its own (its error renders inside the form).
  const mutations = useLimitMutations();

  const orgNameById = useMemo(
    () => new Map(orgs.organizations.map((org) => [org.id, org.name])),
    [orgs.organizations],
  );

  const scopeLabels = useMemo(
    () => ({
      label: t("scope_label"),
      placeholder: t("scope_placeholder"),
      loading: t("scope_loading"),
      empty: t("scope_empty"),
      searching: t("scope_searching"),
      searchError: t("scope_search_error"),
      catalogError: t("scope_catalog_error"),
      catalogTruncated: t("scope_catalog_truncated"),
      globalOption: t("scope_global_option"),
      globalHint: t("scope_global_hint"),
      orgHint: t("scope_org_hint"),
    }),
    [t],
  );

  const overrideCount = useMemo(
    () =>
      scopeOrgId
        ? state.cells.filter((cell) => cell.organization.length > 0).length
        : state.kpis.cellsWithOverrides,
    [scopeOrgId, state.cells, state.kpis.cellsWithOverrides],
  );

  const visibleCells = useMemo(() => {
    if (filter === "limited") {
      return state.cells.filter((cell) =>
        cell.effective.some((limit) => limit.min_amount !== null || limit.max_amount !== null),
      );
    }
    if (filter === "overrides") {
      return scopeOrgId
        ? state.cells.filter((cell) => cell.organization.length > 0)
        : state.cells.filter((cell) => cell.org_overrides.length > 0);
    }
    return state.cells;
  }, [state.cells, filter, scopeOrgId]);

  const payinCells = useMemo(() => visibleCells.filter((cell) => cell.direction === "PAYIN"), [visibleCells]);
  const payoutCells = useMemo(
    () => visibleCells.filter((cell) => cell.direction === "PAYOUT"),
    [visibleCells],
  );

  const handleViewOrg = useCallback((organizationId: string) => {
    setScopeOrgId(organizationId);
    setFilter("all");
  }, []);

  const openEdit = useCallback(
    (cell: AmountLimitCell) => {
      // Seed from the both-kinds row when there is one; otherwise from the first typed row, so a corridor
      // configured per kind (the PSE seed) opens on a row that exists instead of an empty form.
      const rows = rowsInScope(cell, scopeOrgId);
      const payerType = rows.some((row) => row.payer_type === null) ? null : (rows[0]?.payer_type ?? null);
      setEditIntent({
        cell,
        organizationId: scopeOrgId,
        organizationName: scopeOrgId ? orgNameById.get(scopeOrgId) : undefined,
        payerType,
      });
    },
    [scopeOrgId, orgNameById],
  );

  const handleSaved = useCallback(
    (railLabel: string) => {
      setEditIntent(null);
      colocaToast.success(t("toast_saved", { rail: railLabel }));
      state.refresh();
    },
    [state, t],
  );

  const openRemove = useCallback(
    (cell: AmountLimitCell) => {
      mutations.clearError();
      setRemoveTarget(cell);
    },
    [mutations],
  );

  // Every live row of the cell in the current scope goes, one DELETE per exact scope; the first failure
  // stops the loop and shows in the dialog, with the rows already removed reflected on the next refresh.
  const handleRemoveConfirm = useCallback(async () => {
    if (!removeTarget) return;
    const rows = scopeOrgId ? removeTarget.organization : removeTarget.global;
    for (const row of rows) {
      const failure = await mutations.clearLimit(removeTarget.rail, {
        direction: removeTarget.direction,
        country: removeTarget.country,
        currency: removeTarget.currency,
        payer_type: row.payer_type,
        organization_id: scopeOrgId,
      });
      if (failure) {
        state.refresh();
        return;
      }
    }
    setRemoveTarget(null);
    colocaToast.success(t("toast_cleared", { rail: formatRail(removeTarget.rail) }));
    state.refresh();
  }, [removeTarget, scopeOrgId, mutations, state, t]);

  if (!state.hasAccess) return <NoAccessView />;

  const removeRows = removeTarget ? (scopeOrgId ? removeTarget.organization : removeTarget.global) : [];

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <ColocaButton
            variant="default"
            size="sm"
            icon={RefreshCw}
            aria-label={t("refresh_aria")}
            className={cn(state.isRefreshing && "[&_svg]:animate-spin")}
            onClick={state.refresh}
          >
            {t("refresh_button")}
          </ColocaButton>
        }
      />

      <OrganizationScopePicker
        id="amount-limits-scope"
        className="max-w-md"
        value={scopeOrgId}
        onValueChange={(organizationId) => {
          setScopeOrgId(organizationId);
          setFilter("all");
        }}
        catalog={orgs}
        labels={scopeLabels}
      />

      {state.hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("error_load")}
          text={t("error_load_detail")}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={state.refresh}>
              {t("error_retry")}
            </ColocaButton>
          }
        />
      ) : (
        <>
          {state.isLoading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[0, 1, 2].map((index) => (
                <ColocaSkeleton key={index} className="h-[5.5rem] rounded-xl" />
              ))}
            </div>
          ) : (
            // The strip IS the filter: each tile narrows the table to the corridors it counts.
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <ColocaKpiCard
                icon={Ruler}
                accent="primary"
                label={t("kpi_all")}
                value={String(state.kpis.totalCells)}
                selected={filter === "all"}
                onSelect={() => setFilter("all")}
              />
              <ColocaKpiCard
                icon={SlidersHorizontal}
                accent={state.kpis.limitedCells > 0 ? "info" : "neutral"}
                label={t("kpi_limited")}
                value={String(state.kpis.limitedCells)}
                selected={filter === "limited"}
                onSelect={() => setFilter("limited")}
              />
              <ColocaKpiCard
                icon={Building2}
                accent={overrideCount > 0 ? "info" : "neutral"}
                label={t(scopeOrgId ? "kpi_own_overrides" : "kpi_overrides")}
                value={String(overrideCount)}
                selected={filter === "overrides"}
                onSelect={() => setFilter("overrides")}
              />
            </div>
          )}

          {!state.isLoading && visibleCells.length === 0 ? (
            <ColocaEmpty icon={Ruler} title={t("empty_title")} description={t("empty_description")} />
          ) : (
            [
              {
                key: "payins",
                icon: ArrowDownToLine,
                titleKey: "section_payins" as const,
                cells: payinCells,
                skeletonRows: 7,
              },
              {
                key: "payouts",
                icon: ArrowUpFromLine,
                titleKey: "section_payouts" as const,
                cells: payoutCells,
                skeletonRows: 5,
              },
            ].map(({ key, icon: SectionIcon, titleKey, cells, skeletonRows }) => {
              if (!state.isLoading && cells.length === 0) return null;
              return (
                <section key={key} className="space-y-3">
                  <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <SectionIcon className="size-4 text-muted-foreground" />
                    {t(titleKey)}
                  </h2>
                  {isMobile ? (
                    <AmountLimitsCards
                      cells={cells}
                      isLoading={state.isLoading}
                      scopeOrgId={scopeOrgId}
                      orgNameById={orgNameById}
                      isMutating={mutations.isSubmitting || state.isRefreshing}
                      onEdit={openEdit}
                      onRemove={openRemove}
                      onViewOrg={handleViewOrg}
                    />
                  ) : (
                    <AmountLimitsTable
                      cells={cells}
                      isLoading={state.isLoading}
                      skeletonRows={skeletonRows}
                      scopeOrgId={scopeOrgId}
                      orgNameById={orgNameById}
                      isMutating={mutations.isSubmitting || state.isRefreshing}
                      onEdit={openEdit}
                      onRemove={openRemove}
                      onViewOrg={handleViewOrg}
                    />
                  )}
                </section>
              );
            })
          )}

          <ColocaAlert variant="secondary" icon={Info} text={t("why_banner")} />
        </>
      )}

      {editIntent && (
        <EditLimitDialog intent={editIntent} open onClose={() => setEditIntent(null)} onSaved={handleSaved} />
      )}

      {removeTarget && (
        <ColocaAlertDialog
          open
          onOpenChange={(next) => {
            if (!next && !mutations.isSubmitting) setRemoveTarget(null);
          }}
          title={t("clear_title")}
          description={t(scopeOrgId ? "clear_override_description" : "clear_description")}
          confirmLabel={t("clear_confirm")}
          confirmDisabled={mutations.isSubmitting}
          cancelLabel={t("clear_cancel")}
          onConfirm={() => void handleRemoveConfirm()}
          onCancel={() => setRemoveTarget(null)}
        >
          <div className="space-y-3">
            <div className="space-y-1 text-sm">
              <p className="text-muted-foreground">{t("clear_rows_intro")}</p>
              <ul className="space-y-0.5 font-mono text-xs tabular-nums">
                {removeRows.map((row) => (
                  <li key={row.id} className="flex justify-between gap-3">
                    <span className="font-sans">
                      {t(
                        row.payer_type === "PERSON"
                          ? "payer_person"
                          : row.payer_type === "COMPANY"
                            ? "payer_company"
                            : "payer_both",
                      )}
                    </span>
                    <span>
                      {t("bound_min")}{" "}
                      {formatBound(row.min_amount, removeTarget.currency, activeLanguage, "—")} ·{" "}
                      {t("bound_max")}{" "}
                      {formatBound(row.max_amount, removeTarget.currency, activeLanguage, "—")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            {mutations.error && <ColocaAlert variant="destructive" text={mutations.error} />}
          </div>
        </ColocaAlertDialog>
      )}
    </div>
  );
};

export default AmountLimitsPage;
